// Catalog.ID Client — `create-mail-address` consent modal
// ============================================================================
// WHY THIS MODE HAS A DIALOG WHEN `issue-mail-cert` DOES NOT
//
// `mail-cert-handlers.ts` argues (correctly, inheriting chat's reasoning) that
// a per-request prompt on cert issuance would fire on every device every 60
// days and train users to click through the one prompt that matters. None of
// that applies here:
//
//   1. It MUTATES durable account state — a new claim under this account
//      binding a public identifier to the key that inbound mail is encrypted
//      to. `issue-mail-cert` reads one bag entry and signs; nothing persists.
//   2. It is once per address, not once per renewal cycle. There is no
//      click-through treadmill to build.
//   3. The record is a PUBLIC IDENTIFIER. Mail deliberately does not model
//      "consent to be mailed" (migration §5.3) — that is what mail is — but
//      "which address is mine, and which key routes it" is still the user's
//      decision, not the mail backend's.
//
// WHAT THE DIALOG MUST NAME, and why each line is here
//
//   * the ADDRESS — the whole decision is "this address, under this account".
//     A dialog that says "create your mail address claim?" without saying WHICH
//     address is not a decision, it is a confirmation reflex. This is the one
//     line `.l1-notes.md` §2 pins explicitly.
//   * the SIGNING ACCOUNT — the same C7 finding the ceremony and key-publication
//     modals record: an approval binds to whoever is logged in, and a claim
//     approved under the wrong identity records the wrong ownership.
//   * the OPERATOR and the mail_encrypt fingerprint — this is *what* the record
//     asserts: where the address lives, and which key inbound mail to it will
//     be encrypted to. Substituting that key is the attack the digest binding
//     and this line both exist to make visible.
//   * that the claim is created but NOT published here. Publication is what
//     turns the record into a directory-visible public identifier, and it is a
//     separate, deliberate act on the ordinary claim surface — promising it
//     here would be a lie, and doing it silently would be worse.
//
// No policy slot ('session' / 'always'): a standing "always create mail address
// claims" grant would let a compromised mail backend bind addresses and keys
// under the account without ever being seen again.

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';

export type MailAddressConsentDecision = 'approve' | 'decline';

export interface MailAddressConsentInput {
    /** The logged-in account the claim is written under. */
    owningCid: string;
    /** The RFC 5321 address the record names — the decision. */
    address: string;
    /** Hub-attested requester (pinned to `mail` upstream; rendered, not trusted). */
    requesterId: string;
    /** Home operator CODE from the claim value — never a URL. */
    operatorId: string;
    /** Fingerprint of the `mail_encrypt` key inside the value — the key inbound
     *  mail to this address gets encrypted to. */
    mailEncryptFingerprint: string;
}

export function showMailAddressConsent(
    shell: Shell,
    input: MailAddressConsentInput,
): Promise<MailAddressConsentDecision> {
    return new Promise<MailAddressConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: MailAddressConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        // The address is the decision, so it is in the title. `textContent`
        // (never innerHTML) throughout: an address arrives over a network and is
        // display text, never markup.
        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: `Record ${input.address} as your mail address?`,
        }));

        const accountGroup = createFieldGroup({ title: 'Recording under' });

        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);

        const requesterRow = createFormRow({ label: 'Requested by' });
        requesterRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.requesterId,
        }));
        accountGroup.body.appendChild(requesterRow.element);
        container.appendChild(accountGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent:
                'This records which mail address is yours and which key mail sent to it '
                + 'is encrypted with. It is created as a private claim — publishing it, '
                + 'which is what makes the address findable, is a separate step you take later.',
        }));

        const recordGroup = createFieldGroup({ title: 'What the record says' });

        const addressRow = createFormRow({ label: 'Mail address' });
        addressRow.content.appendChild(createElement('code', { textContent: input.address }));
        recordGroup.body.appendChild(addressRow.element);

        const operatorRow = createFormRow({ label: 'Home operator' });
        operatorRow.content.appendChild(createElement('code', {
            textContent: input.operatorId,
        }));
        recordGroup.body.appendChild(operatorRow.element);

        const keyRow = createFormRow({ label: 'Mail encryption key' });
        keyRow.content.appendChild(createElement('code', {
            textContent: shortFp(input.mailEncryptFingerprint),
            attributes: { title: input.mailEncryptFingerprint },
        }));
        recordGroup.body.appendChild(keyRow.element);

        // Deliberately not "a NEW claim": whether an existing `mail_address`
        // claim with this exact value is reused can only be known after the
        // keybag is unlocked and the candidate values decrypted, which happens
        // AFTER this dialog. Promising one or the other here would be a guess
        // shown as a fact.
        const claimRow = createFormRow({ label: 'Claim' });
        claimRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: 'Your mail address claim — created now if you have none for this address',
        }));
        recordGroup.body.appendChild(claimRow.element);

        container.appendChild(recordGroup.element);

        const actions = createElement('div', { className: 'cs-form-actions' });
        const btnDecline = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Decline',
        }) as HTMLButtonElement;
        btnDecline.addEventListener('click', () => resolveOnce('decline'));
        actions.appendChild(btnDecline);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Record',
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('approve'));
        actions.appendChild(btnApprove);
        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal. Never a silent approval, and
            // never a hang: the mail backend is waiting on this promise.
            onClose: () => resolveOnce('decline'),
        });
    });
}

function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}
